// 라우팅 + 접근 제어
//  - 미로그인 → /login
//  - 약관 미동의(PENDING_TERMS) → /terms 로 강제 유도(서비스 이용 불가 상태이므로)
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { ReactElement } from "react";
import { AuthProvider, useAuth } from "@taplink/web-shared";
import { LoginPage } from "./pages/LoginPage.tsx";
import { SignupPage } from "./pages/SignupPage.tsx";
import { SocialCallbackPage } from "./pages/SocialCallbackPage.tsx";
import { TermsAgreementPage } from "./pages/TermsAgreementPage.tsx";
import { LinksPage } from "./pages/LinksPage.tsx";
import { AccountPage } from "./pages/AccountPage.tsx";
import { StatsPage } from "./pages/StatsPage.tsx";
import { TenantAuditPage } from "./pages/TenantAuditPage.tsx";
import { TeamPage } from "./pages/TeamPage.tsx";
import { InvitationAcceptPage } from "./pages/InvitationAcceptPage.tsx";
import { peekInvitation } from "./api/pendingInvitation.ts";
import { AppLayout } from "./components/AppLayout.tsx";
import {
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "./pages/PasswordPages.tsx";

function Loading() {
  return (
    <main className="page">
      <div className="card">
        <p role="status">불러오는 중…</p>
      </div>
    </main>
  );
}

/** 로그인 + 약관 동의를 모두 마친 사용자만 접근 가능 */
function Protected({ children }: { children: ReactElement }) {
  const { me, loading } = useAuth();
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (!me.termsAgreed) return <Navigate to="/terms" replace />;

  // ⚠️ 맡겨 둔 초대가 있으면 **수락 화면으로 되돌린다.**
  //
  //    초대받은 사람은 대개 계정이 없어 가입 절차로 빠지는데, 예전에는 가입을 마치면
  //    그냥 홈으로 갔다 — 초대는 대기 상태로 남고 자기 개인 공간만 새로 생겼다
  //    (실측 운영 2026-08-24: 초대 18:11, 가입 18:15, TENANT_JOINED 없음).
  //
  //    여기에 두는 이유: 로그인·가입·**소셜 로그인** 어느 길로 들어와도 결국 이 관문을
  //    지난다. 각 화면의 성공 후 이동을 고치면 한 곳을 빠뜨리게 되고, 소셜 콜백은
  //    리다이렉트 체인이라 `next` 파라미터를 실어 보내기도 어렵다.
  if (peekInvitation()) return <Navigate to="/invitations/accept" replace />;

  return children;
}

/** 로그인은 했으나 약관 동의만 남은 상태 */
function TermsOnly({ children }: { children: ReactElement }) {
  const { me, loading } = useAuth();
  if (loading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  // 약관을 마치면 홈이 아니라 **Protected** 로 간다 — 거기서 맡긴 초대를 집어낸다
  if (me.termsAgreed) return <Navigate to="/" replace />;
  return children;
}

/** 이미 로그인했다면 로그인·가입 화면 대신 홈으로 */
function AnonymousOnly({ children }: { children: ReactElement }) {
  const { me, loading } = useAuth();
  if (loading) return <Loading />;
  if (me) {
    // 맡겨 둔 초대가 있으면 홈이 아니라 수락 화면이다 — 로그인 화면을 거쳐 온 경우
    if (me.termsAgreed && peekInvitation()) return <Navigate to="/invitations/accept" replace />;
    return <Navigate to={me.termsAgreed ? "/" : "/terms"} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
      <Route path="/login" element={<AnonymousOnly><LoginPage /></AnonymousOnly>} />
      <Route path="/signup" element={<AnonymousOnly><SignupPage /></AnonymousOnly>} />
      <Route path="/forgot-password" element={<AnonymousOnly><ForgotPasswordPage /></AnonymousOnly>} />

      {/* 메일 링크로 진입하는 화면 — 로그인 여부와 무관 */}
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />

      {/* 소셜 로그인 콜백 — 백엔드가 토큰을 프래그먼트로 전달 */}
      <Route path="/auth/callback" element={<SocialCallbackPage />} />

      <Route path="/terms" element={<TermsOnly><TermsAgreementPage /></TermsOnly>} />

      {/* 로그인 + 약관 동의를 마친 사용자 영역 */}
      <Route path="/" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<LinksPage />} />
        <Route path="stats" element={<StatsPage />} />
        <Route path="account" element={<AccountPage />} />

        {/* 운영 관리 — SYSTEM_ADMIN */}
        <Route path="team" element={<TeamPage />} />
        <Route path="activity" element={<TenantAuditPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  // ⚠️ basename 은 vite 의 base 와 **짝**이다(vite.config.ts). 한쪽만 바꾸면
  //    자산은 받아지는데 모든 라우트가 어긋나 빈 화면이 뜬다.
  return (
    <BrowserRouter basename="/console">
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
